"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.importQuestionsQuerySchema = exports.QUESTION_IMPORT_MESSAGES = exports.QUESTION_IMPORT_HEADERS = exports.QUESTION_IMPORT_OPTION_COLUMNS = exports.QUESTION_IMPORT_COLUMNS = exports.QUESTION_IMPORT_FIRST_ROW = exports.QUESTION_IMPORT_MAX_ROWS = void 0;
exports.questionTypeOf = questionTypeOf;
exports.questionFromRow = questionFromRow;
exports.checkQuestionRows = checkQuestionRows;
exports.hasQuestionColumn = hasQuestionColumn;
const zod_1 = require("zod");
const assessments_1 = require("./assessments");
const schemas_1 = require("./schemas");
// F9.17 — questions from a spreadsheet (ADR 0047, DECISIONS-LOG 2026-10-16 Q1). One row is
// one question; good rows are imported and bad rows are listed by their row number.
/** One file's worth; a bank of thousands is uploaded in parts. */
exports.QUESTION_IMPORT_MAX_ROWS = 500;
/** The spreadsheet's first data row is row 2: row 1 names the columns. */
exports.QUESTION_IMPORT_FIRST_ROW = 2;
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
/** The template's columns, in order. Matching ignores case and spaces. */
exports.QUESTION_IMPORT_COLUMNS = {
    question: 'Question',
    type: 'Type',
    marks: 'Marks',
    difficulty: 'Difficulty',
    skills: 'Skills',
    roleFamily: 'Role family',
    correct: 'Correct',
    acceptedAnswers: 'Accepted answers',
    explanation: 'Explanation',
};
exports.QUESTION_IMPORT_OPTION_COLUMNS = OPTION_LETTERS.slice(0, assessments_1.QUESTION_OPTIONS_MAX).map((letter) => `Option ${letter}`);
exports.QUESTION_IMPORT_HEADERS = [
    exports.QUESTION_IMPORT_COLUMNS.question,
    exports.QUESTION_IMPORT_COLUMNS.type,
    exports.QUESTION_IMPORT_COLUMNS.marks,
    exports.QUESTION_IMPORT_COLUMNS.difficulty,
    exports.QUESTION_IMPORT_COLUMNS.skills,
    exports.QUESTION_IMPORT_COLUMNS.roleFamily,
    ...exports.QUESTION_IMPORT_OPTION_COLUMNS,
    exports.QUESTION_IMPORT_COLUMNS.correct,
    exports.QUESTION_IMPORT_COLUMNS.acceptedAnswers,
    exports.QUESTION_IMPORT_COLUMNS.explanation,
];
/** Skills are split on commas; accepted answers on semicolons, since an answer may hold a comma. */
const SKILL_SEPARATOR = ',';
const ANSWER_SEPARATOR = ';';
const LETTER_SEPARATOR = /[\s,;]+/;
/** The words a person might type in "Type", beyond the bank's own labels. */
const TYPE_WORDS = {
    'multiple choice': 'single_select',
    'one answer': 'single_select',
    single: 'single_select',
    mcq: 'single_select',
    'all that apply': 'multi_select',
    multi: 'multi_select',
    multiple: 'multi_select',
    'short answer': 'text',
    short: 'text',
    'long answer': 'long_text',
    long: 'long_text',
    file: 'file',
    'file upload': 'file',
};
exports.QUESTION_IMPORT_MESSAGES = {
    noQuestionColumn: 'The file needs a "Question" column. Download the template and copy your questions into it.',
    tooManyRows: `The most per file is ${exports.QUESTION_IMPORT_MAX_ROWS} questions — split it and upload the parts.`,
    questionMissing: 'The question is empty.',
    typeUnknown: 'Type must be one of: one answer, all that apply, short answer, long answer, file upload.',
    marksInvalid: 'Marks must be a whole number from 1 to 100.',
    difficultyUnknown: 'Difficulty must be Easy, Medium or Hard, or left empty.',
    correctUnknown: 'Correct must name the right option letters, e.g. B or A, C.',
    correctMissingOption: 'Correct names a letter whose option is empty.',
    nothingToImport: 'No row in this file can be imported. Fix the rows listed and upload again.',
};
/** F9.17's second step sends the same file again with the venture to add it to. */
exports.importQuestionsQuerySchema = zod_1.z.object({ ventureId: schemas_1.uuidSchema });
function headerKey(header) {
    return header.toLowerCase().replace(/[^a-z]/g, '');
}
function cellOf(row, header) {
    const key = headerKey(header);
    const match = Object.keys(row).find((candidate) => headerKey(candidate) === key);
    return match === undefined ? '' : (row[match] ?? '').trim();
}
function listOf(text, separator) {
    return text
        .split(separator)
        .map((part) => part.trim())
        .filter((part) => part !== '');
}
function questionTypeOf(text) {
    const word = text.trim().toLowerCase().replace(/\s+/g, ' ');
    if (word === '')
        return null;
    const byLabel = assessments_1.ASSESSMENT_QUESTION_TYPES.find((type) => assessments_1.ASSESSMENT_QUESTION_TYPE_LABELS[type].toLowerCase() === word);
    if (byLabel)
        return byLabel;
    return TYPE_WORDS[word] ?? TYPE_WORDS[word.replace(/^multiple choice\s*[—–-]\s*/, '')] ?? null;
}
function difficultyOf(text) {
    const word = text.trim().toLowerCase();
    if (word === '')
        return null;
    return assessments_1.QUESTION_DIFFICULTIES.find((difficulty) => difficulty === word);
}
/**
 * A blank type is read from the row: options make it multiple choice (several correct
 * letters make it "all that apply"), accepted answers make it a short answer.
 */
function guessedType(correctLetters, options, acceptedAnswers) {
    if (options.length > 0)
        return correctLetters.length > 1 ? 'multi_select' : 'single_select';
    return acceptedAnswers.length > 0 ? 'text' : 'long_text';
}
/** Turns one spreadsheet row into a question, or says everything wrong with it. */
function questionFromRow(row) {
    const problems = [];
    const questionText = cellOf(row, exports.QUESTION_IMPORT_COLUMNS.question);
    if (questionText === '')
        problems.push(exports.QUESTION_IMPORT_MESSAGES.questionMissing);
    const optionCells = exports.QUESTION_IMPORT_OPTION_COLUMNS.map((header) => cellOf(row, header));
    const filledOptions = optionCells.filter((text) => text !== '');
    const correctText = cellOf(row, exports.QUESTION_IMPORT_COLUMNS.correct).toUpperCase();
    const correctLetters = listOf(correctText, LETTER_SEPARATOR);
    const acceptedAnswers = listOf(cellOf(row, exports.QUESTION_IMPORT_COLUMNS.acceptedAnswers), ANSWER_SEPARATOR);
    const typeText = cellOf(row, exports.QUESTION_IMPORT_COLUMNS.type);
    const type = typeText === '' ? guessedType(correctLetters, filledOptions, acceptedAnswers) : questionTypeOf(typeText);
    if (type === null)
        problems.push(exports.QUESTION_IMPORT_MESSAGES.typeUnknown);
    const marksText = cellOf(row, exports.QUESTION_IMPORT_COLUMNS.marks);
    const points = marksText === '' ? 1 : Number(marksText);
    if (!Number.isInteger(points) || points < 1 || points > 100)
        problems.push(exports.QUESTION_IMPORT_MESSAGES.marksInvalid);
    const difficulty = difficultyOf(cellOf(row, exports.QUESTION_IMPORT_COLUMNS.difficulty));
    if (difficulty === undefined)
        problems.push(exports.QUESTION_IMPORT_MESSAGES.difficultyUnknown);
    const letterIndexes = correctLetters.map((letter) => OPTION_LETTERS.findIndex((known) => known === letter));
    if (letterIndexes.some((index) => index < 0))
        problems.push(exports.QUESTION_IMPORT_MESSAGES.correctUnknown);
    else if (letterIndexes.some((index) => optionCells[index] === ''))
        problems.push(exports.QUESTION_IMPORT_MESSAGES.correctMissingOption);
    if (problems.length > 0 || type === null || difficulty === undefined)
        return { problems };
    const isChoice = (0, assessments_1.isChoiceQuestion)(type);
    const options = isChoice
        ? optionCells.flatMap((text, index) => (text === '' ? [] : [{ text, isCorrect: letterIndexes.includes(index) }]))
        : filledOptions.map((text) => ({ text, isCorrect: false }));
    const explanation = cellOf(row, exports.QUESTION_IMPORT_COLUMNS.explanation);
    const roleFamily = cellOf(row, exports.QUESTION_IMPORT_COLUMNS.roleFamily);
    const parsed = assessments_1.questionInputSchema.safeParse({
        questionText,
        type,
        points,
        difficulty,
        roleFamily: roleFamily === '' ? null : roleFamily,
        tags: [...new Set(listOf(cellOf(row, exports.QUESTION_IMPORT_COLUMNS.skills), SKILL_SEPARATOR))],
        options,
        acceptedAnswers,
        explanation: explanation === '' ? null : explanation,
    });
    if (!parsed.success)
        return { problems: [...new Set(parsed.error.issues.map((issue) => issueMessage(issue.path, issue.message)))] };
    return { question: parsed.data };
}
/** The schema's own words, with the column named where the schema only names a field. */
function issueMessage(path, message) {
    const field = String(path[0] ?? '');
    const column = {
        questionText: exports.QUESTION_IMPORT_COLUMNS.question,
        tags: exports.QUESTION_IMPORT_COLUMNS.skills,
        roleFamily: exports.QUESTION_IMPORT_COLUMNS.roleFamily,
        acceptedAnswers: exports.QUESTION_IMPORT_COLUMNS.acceptedAnswers,
        explanation: exports.QUESTION_IMPORT_COLUMNS.explanation,
        options: 'Options',
    };
    const name = column[field];
    return name ? `${name}: ${message}` : message;
}
/** The whole file: every good row as a question, every bad row by its spreadsheet number. */
function checkQuestionRows(rows) {
    const questions = [];
    const problems = [];
    rows.forEach((row, index) => {
        const result = questionFromRow(row);
        if ('question' in result)
            questions.push(result.question);
        else
            problems.push({ row: index + exports.QUESTION_IMPORT_FIRST_ROW, message: result.problems.join(' ') });
    });
    return { questions, problems, totalRows: rows.length };
}
function hasQuestionColumn(headers) {
    return headers.some((header) => headerKey(header) === headerKey(exports.QUESTION_IMPORT_COLUMNS.question));
}
//# sourceMappingURL=question-import.js.map